import { Link, useLocation } from "react-router-dom";
import { Instagram, Linkedin, Facebook, Twitter, Mail, MessageCircle } from "lucide-react";
import { SITE } from "@/config/site";
import { useProjects } from "@/hooks/useProjects";
import { Logo } from "./Navbar";
import { Parallax } from "./Motion";

const socialIcons = { instagram: Instagram, linkedin: Linkedin, facebook: Facebook, x: Twitter };

export const Footer = () => {
  const { projects } = useProjects();
  const { pathname } = useLocation();
  const prefix = pathname === "/" ? "" : "/";
  const socials = Object.entries(SITE.socials).filter(([, url]) => url);

  return (
    <footer data-testid="site-footer" className="bg-[#0F172A] pt-16 text-white">
      <div className="container-x grid gap-12 pb-12 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1fr]">
        <div>
          <Logo light />
          <p className="mt-5 max-w-xs text-slate-300">{SITE.tagline}.</p>
          <div className="mt-6 flex gap-3">
            <a data-testid="footer-whatsapp" aria-label="WhatsApp" href={`https://wa.me/${SITE.contact.whatsapp}`} target="_blank" rel="noopener noreferrer" className="grid h-11 w-11 place-items-center rounded-full bg-[#84CC16] text-slate-900 transition-transform hover:-translate-y-0.5"><MessageCircle className="h-5 w-5" /></a>
            <a data-testid="footer-email" aria-label="Email" href={`mailto:${SITE.contact.email}`} className="grid h-11 w-11 place-items-center rounded-full bg-[#FACC15] text-slate-900 transition-transform hover:-translate-y-0.5"><Mail className="h-5 w-5" /></a>
            {socials.map(([k, url]) => {
              const Icon = socialIcons[k];
              return <a key={k} data-testid={`footer-social-${k}`} aria-label={k} href={url} target="_blank" rel="noopener noreferrer" className="grid h-11 w-11 place-items-center rounded-full bg-white/10 transition-colors hover:bg-[#0055FF]"><Icon className="h-5 w-5" /></a>;
            })}
          </div>
        </div>
        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-[#FACC15]">Navigate</h3>
          <ul className="mt-5 space-y-3">{SITE.nav.map((n) => <li key={n.href}><a data-testid={`footer-nav-${n.label.toLowerCase()}`} href={`${prefix}${n.href}`} className="text-slate-300 transition-colors hover:text-white">{n.label}</a></li>)}</ul>
        </div>
        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-[#FF5566]">Projects</h3>
          <ul className="mt-5 space-y-3">{projects.map((p) => <li key={p.id}><Link data-testid={`footer-project-${p.slug}`} to={`/work/${p.slug}`} className="text-slate-300 transition-colors hover:text-white">{p.title}</Link></li>)}</ul>
        </div>
        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-[#84CC16]">Contact</h3>
          <ul className="mt-5 space-y-3 text-slate-300">
            <li><a href={`mailto:${SITE.contact.email}`} className="break-all hover:text-white">{SITE.contact.email}</a></li>
            <li><a href={`tel:${SITE.contact.phone.replace(/\s/g, "")}`} className="hover:text-white">{SITE.contact.phone}</a></li>
          </ul>
        </div>
      </div>
      <div className="overflow-hidden">
        <Parallax speed={40}>
          <p className="select-none whitespace-nowrap text-center font-display text-[14vw] font-extrabold leading-[0.85] tracking-tighter text-white/[0.07]" aria-hidden="true">{SITE.brand}</p>
        </Parallax>
      </div>
      <div className="border-t border-white/10">
        <div className="container-x flex flex-col justify-between gap-2 py-6 text-sm text-slate-400 sm:flex-row">
          <p>© {new Date().getFullYear()} {SITE.brand}. All rights reserved.</p>
          <Link to="/admin" data-testid="footer-admin-link" className="hover:text-white">Admin</Link>
        </div>
      </div>
    </footer>
  );
};
